import React, { createContext, useContext, useState, ReactNode } from 'react';

interface User {
  id: string;
  email: string;
  name: string;
  username?: string;
  role?: string;
  first_login?: boolean;
  app_roles?: {
    fr_update?: string;
    translate?: string;
  };
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  // Rol kontrol fonksiyonları
  isAdmin: () => boolean;
  isEditor: () => boolean;
  isRedactor: () => boolean;
  canAccessAdminPanel: () => boolean;
  canAccessTranslateAdminPanel: () => boolean;
  canAccessFRUpdateAdminPanel: () => boolean;
  canManageCompanies: () => boolean;
  canManageTranslationSettings: () => boolean;
  canStartNewTranslation: () => boolean;
  canDeleteDocuments: () => boolean;
  canToggleDocumentCompletion: () => boolean;
  canStartQualityControl: () => boolean;
  canDownloadFiles: () => boolean;
  // FR Update yetkileri
  isFRUpdateAdmin: () => boolean;
  canManageFRCompanies: () => boolean;
  canDeleteFRDocuments: () => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      // Webhook'a giriş bilgilerini gönder
      const response = await fetch('https://n8n.mahir.ai/webhook/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password
        })
      });

      if (response.ok) {
        const data = await response.json();
        
        // Webhook yanıtını console'a logla
        console.log('🚀 Webhook login response:', data);
        console.log('📦 app_roles from response:', data.app_roles);
        
        // Yeni webhook yanıt formatını kontrol et
        if (data.success === true) {
          const newUser = {
            id: data.user_id || 'user_' + Date.now().toString(),
            email: data.email || email,
            name: data.username || 'User',
            username: data.username,
            role: data.role,
            first_login: data.first_login,
            app_roles: data.app_roles || {}
          };
          setUser(newUser);
          localStorage.setItem('user', JSON.stringify(newUser));
          console.log('✅ Login successful, user data saved:', newUser);
          console.log('✅ app_roles saved:', newUser.app_roles);
          return true;
        } else {
          console.log('Login failed - webhook returned success: false');
          return false;
        }
      } else {
        console.error('Webhook request failed with status:', response.status);
        return false;
      }
    } catch (error) {
      // Ağ hatası veya diğer hatalar
      console.error('Login network error:', error);
      return false;
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('user');
  };

  // Check for existing session on mount
  React.useEffect(() => {
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      try {
        const parsedUser = JSON.parse(savedUser);
        // Ensure user has an id field for backward compatibility
        if (!parsedUser.id) {
          parsedUser.id = 'user_' + Date.now().toString();
          localStorage.setItem('user', JSON.stringify(parsedUser));
        }
        // Ensure user has app_roles for backward compatibility
        if (!parsedUser.app_roles) {
          parsedUser.app_roles = {};
        }
        setUser(parsedUser);
        console.log('User loaded from localStorage:', parsedUser);
      } catch (error) {
        console.error('Error parsing saved user:', error);
        localStorage.removeItem('user');
      }
    }
    setIsLoading(false);
  }, []);

  const isAuthenticated = !!user;

  // Rol kontrol fonksiyonları - Yeni login sistemi (app_roles.translate)
  const isAdmin = (): boolean => {
    return user?.app_roles?.translate?.toLowerCase() === 'admin';
  };

  const isEditor = (): boolean => {
    return user?.app_roles?.translate?.toLowerCase() === 'editor';
  };

  const isRedactor = (): boolean => {
    return user?.app_roles?.translate?.toLowerCase() === 'redactor';
  };

  const canAccessAdminPanel = (): boolean => {
    return isAdmin();
  };

  const canAccessTranslateAdminPanel = (): boolean => {
    // Translate app için admin rolü kontrolü
    console.log('🔍 canAccessTranslateAdminPanel check:', {
      user: user,
      app_roles: user?.app_roles,
      translate_role: user?.app_roles?.translate,
      result: user?.app_roles?.translate?.toLowerCase() === 'admin'
    });
    return user?.app_roles?.translate?.toLowerCase() === 'admin';
  };

  const canAccessFRUpdateAdminPanel = (): boolean => {
    // FR Update app için admin rolü kontrolü
    console.log('🔍 canAccessFRUpdateAdminPanel check:', {
      user: user,
      app_roles: user?.app_roles,
      fr_update_role: user?.app_roles?.fr_update,
      result: user?.app_roles?.fr_update?.toLowerCase() === 'admin'
    });
    return user?.app_roles?.fr_update?.toLowerCase() === 'admin';
  };

  const canManageCompanies = (): boolean => {
    // Sadece admin şirket ekleyebilir/silebilir/düzenleyebilir
    return isAdmin();
  };

  const canManageTranslationSettings = (): boolean => {
    // Sadece admin çeviri ayarlarını değiştirebilir
    return isAdmin();
  };

  const canStartNewTranslation = (): boolean => {
    // Sadece admin yeni çeviri başlatabilir
    return isAdmin();
  };

  const canDeleteDocuments = (): boolean => {
    // Sadece admin doküman silebilir
    return isAdmin();
  };

  const canToggleDocumentCompletion = (): boolean => {
    // Admin ve editor doküman tamamlanma durumunu değiştirebilir
    return isAdmin() || isEditor();
  };

  const canStartQualityControl = (): boolean => {
    // Sadece admin kalite kontrol başlatabilir
    return isAdmin();
  };

  const canDownloadFiles = (): boolean => {
    // Admin ve editor dosya indirebilir
    return isAdmin() || isEditor();
  };

  // FR Update yetkileri
  const isFRUpdateAdmin = (): boolean => {
    return user?.app_roles?.fr_update?.toLowerCase() === 'admin';
  };

  const canManageFRCompanies = (): boolean => {
    // FR Update'de sadece admin şirket ekleyebilir/silebilir/düzenleyebilir
    return isFRUpdateAdmin();
  };

  const canDeleteFRDocuments = (): boolean => {
    // FR Update'de sadece admin doküman silebilir
    return isFRUpdateAdmin();
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      isAuthenticated, 
      isLoading, 
      login, 
      logout,
      isAdmin,
      isEditor,
      isRedactor,
      canAccessAdminPanel,
      canAccessTranslateAdminPanel,
      canAccessFRUpdateAdminPanel,
      canManageCompanies,
      canManageTranslationSettings,
      canStartNewTranslation,
      canDeleteDocuments,
      canToggleDocumentCompletion,
      canStartQualityControl,
      canDownloadFiles,
      isFRUpdateAdmin,
      canManageFRCompanies,
      canDeleteFRDocuments
    }}>
      {children}
    </AuthContext.Provider>
  );
};
